import React, { useState, useEffect } from 'react';
import HeaderMenu from '../Header/HeaderMenu';
import axiosApi from '../Utils/axiosApi';
import checkPermission from '../Utils/checkPermission';
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  OutlinedInput,
  
} from '@mui/material';

const CreateNotification = ({ isDialog = false, isUpdate = false, initialData = {} }) => {
  const [formData, setFormData] = useState({
    id: '',
    recipientHostelId: '',
    recipientHostelerId: '',
    title: '',
    message: '',
    priority: '',
    type: '',
    reply: '',
    feedbackStatus: '',
    directionType: '',
  });

  const [hostelList, setHostelList] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [loadingHostelers, setLoadingHostelers] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isUpdate && initialData && typeof initialData === 'object') {
      setFormData((prev) => ({
        ...prev,
        id: initialData.id ?? '',
        recipientHostelId: initialData.recipientHostelId?.toString() ?? '',
        recipientHostelerId: initialData.recipientHostelerId?.toString() ?? '',
        title: initialData.title ?? '',
        message: initialData.message ?? '',
        priority: initialData.priority ?? '',
        type: initialData.type ?? '',
        reply: initialData.reply ?? '',
        feedbackStatus: initialData.feedbackStatus ?? '',
        directionType: initialData.directionType ?? '',
		source: initialData.source ?? ''
      }));
    }
  }, [isUpdate, initialData]);

  useEffect(() => {
    axiosApi
      .post('/hostel/getHostelName', {})
      .then((response) => {
        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.hostels || [];
        const formatted = data.map((row) => {
          const id = Array.isArray(row) ? row[0] : row.id ?? row.hostelId;
          const name = Array.isArray(row) ? row[1] : row.hostelName ?? row.name;
          return { id: String(id), hostelName: String(name ?? '') };
        });
        setHostelList(formatted);
      })
      .catch((error) => console.error('Error fetching hostels:', error));
  }, []);

  useEffect(() => {
    if (!formData.recipientHostelId) {
      setUserOptions([]);
      return;
    }
    setLoadingHostelers(true);
    axiosApi
      .post('/hosteler/getHosteler', { hostelIds: [Number(formData.recipientHostelId)] })
      .then((response) => {
        const arr = Array.isArray(response.data) ? response.data : [];
        const normalized = arr.map((u) => ({
          hostelerId: String(u.hostelerId ?? u.id ?? u[0]),
          hostelerName: String(u.hostelerName ?? u.name ?? u[1] ?? ''),
        }));
        setUserOptions(normalized);
      })
      .catch((error) => {
        console.error('Error fetching hostelers:', error);
        setUserOptions([]);
      })
      .finally(() => setLoadingHostelers(false));
  }, [formData.recipientHostelId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const {
      id,
      recipientHostelId,
      recipientHostelerId,
      title,
      message,
      priority,
      type,
      reply,
      feedbackStatus,
    } = formData;

    if (!recipientHostelId) {
      setErrorMessage('Please select a hostel.');
      setSuccessMessage('');
      return;
    }

    const dto = {
      recipientHostelId: Number(recipientHostelId),
      title,
      message,
      priority,
      type,
      reply,
      feedbackStatus,
      ...(recipientHostelerId ? { recipientHostelerId: Number(recipientHostelerId) } : {}),
      ...(isUpdate ? { id } : {}),
    };

    try {
      const url = isUpdate
        ? '/hostelNotification/updateHostelNotification'
        : '/hostelNotification/createHostelNotification';

      await axiosApi.post(url, { hostelNotificationDTO: dto });
      setSuccessMessage(`Notification ${isUpdate ? 'updated' : 'created'} successfully!`);
      setErrorMessage('');
      setFormData({
        id: '',
        recipientHostelId: '',
        recipientHostelerId: '',
        title: '',
        message: '',
        priority: '',
        type: '',
        reply: '',
        feedbackStatus: '',
        directionType: '',
      });
    } catch (error) {
      console.error('Failed to submit notification:', error?.response?.data || error.message);
      setErrorMessage('Failed to submit notification. Please try again.');
      setSuccessMessage('');
    }
  };

  const showFeedbackFields = formData.type === 'FEEDBACK';

  // ✅ Permission rules
  const isSent = formData.directionType === 'SENT';
  const hasSpecialPermission = checkPermission('Notification', 'Notifications', 'SPECIAL_UPDATE');
  const isSystem = formData.source === 'SYSTEM';
  // If update, restrict editing based on directionType
  const disableGeneralFields = isUpdate && !isSent && !hasSpecialPermission ;

  return (
    <div className="create-notification">
      {!isDialog && <HeaderMenu />}
	  
      <Paper elevation={2} sx={{ p: 3 }}>
        {successMessage && <Alert severity="success" sx={{ mb: 2 }}>{successMessage}</Alert>}
        {errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert>}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Hostel */}
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required disabled={disableGeneralFields}>
                <InputLabel>Recipient Hostel</InputLabel>
                <Select
                  name="recipientHostelId"
                  value={formData.recipientHostelId}
                  onChange={handleChange}
                  input={<OutlinedInput label="Recipient Hostel" />}
                >
                  {hostelList.map((hostel) => (
                    <MenuItem key={hostel.id} value={hostel.id}>
                      {hostel.hostelName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Hosteler */}
            <Grid item xs={12}>
              <FormControl
                fullWidth
                size="small"
                disabled={!formData.recipientHostelId || loadingHostelers || disableGeneralFields}
              >
                <InputLabel>Recipient Hosteler</InputLabel>
                <Select
                  name="recipientHostelerId"
                  value={formData.recipientHostelerId}
                  onChange={handleChange}
                  input={<OutlinedInput label="Recipient Hosteler" />}
                >
                  <MenuItem value="">
                    <em>All Hostelers</em>
                  </MenuItem>
                  {userOptions.map((u) => (
                    <MenuItem key={u.hostelerId} value={u.hostelerId}>
                      {u.hostelerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Title */}
            <Grid item xs={12}>
              <TextField
                name="title"
                value={formData.title}
                onChange={handleChange}
                fullWidth
                label="Title"
                size="small"
                required
                disabled={disableGeneralFields}
              />
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <TextField
                name="message"
                value={formData.message}
                onChange={handleChange}
                fullWidth
                label="Message"
                multiline
                rows={3}
                size="small"
                required
                disabled={disableGeneralFields}
              />
            </Grid>

            {/* Priority */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small" required disabled={disableGeneralFields}>
                <InputLabel>Priority</InputLabel>
                <Select name="priority" value={formData.priority} onChange={handleChange}>
                  <MenuItem value="HIGH">High</MenuItem>
                  <MenuItem value="MEDIUM">Medium</MenuItem>
                  <MenuItem value="LOW">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Type */}
            <Grid item xs={6} sm={3}>
              <FormControl fullWidth size="small" required disabled={disableGeneralFields}>
                <InputLabel>Type</InputLabel>
                <Select name="type" value={formData.type} onChange={handleChange}>
                  <MenuItem value="ALERT">Alert</MenuItem>
                  <MenuItem value="REMINDER">Reminder</MenuItem>
                  <MenuItem value="INFO">Info</MenuItem>
                  <MenuItem value="FEEDBACK">Feedback</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Feedback fields */}
            {showFeedbackFields && (
              <>
                <Grid item xs={12}>
                  <TextField
                    name="reply"
                    value={formData.reply}
                    onChange={handleChange}
                    fullWidth
                    label="Reply"
                    multiline
                    rows={2}
                    size="small"
                    disabled={isUpdate && !isSent && !hasSpecialPermission ? false : false}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <FormControl fullWidth size="small" disabled={isUpdate && !isSent && !hasSpecialPermission ? false : false}>
                    <InputLabel>feedbackStatus</InputLabel>
                    <Select name="feedbackStatus" value={formData.feedbackStatus} onChange={handleChange}>
                      <MenuItem value="OPEN">Open</MenuItem>
                      <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
                      <MenuItem value="CLOSED">Closed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Submit */}
            <Grid item xs={12}>
            <Button
  type="submit"
  fullWidth
  variant="contained"
  color="primary"
  disabled={
    isUpdate
      ? (
          isSystem || // SYSTEM always blocked
          !(
            (formData.directionType === "SENT" &&
              checkPermission('Notification', 'Notifications', 'UPDATE')) ||
            hasSpecialPermission
          )
        )
      : !checkPermission('Notification', 'Notifications', 'CREATE')
  }
>
  {isUpdate ? "Update Notification" : "Create Notification"}
</Button>



            </Grid>
          </Grid>
        </form>
      </Paper>

    </div>
  );
};

export default CreateNotification;
